import { put, take, fork, call, select, all } from "redux-saga/effects";
import { ClaimAction, ClaimScope } from "./types";
import {
  enableScopeLoading,
  disableScopeLoading,
} from "../scope-loading/actions";
import { ScopeErrorAction } from "../scope-error/types";
import {
  makeHTMLContentForTicket,
  mapApiClaimData,
  mapCustomerInfoToApi,
  mapZohoToDocuments,
  nextStepRedirect,
} from "./helpers";
import * as API from "../../service/api";
import { updateClaimState } from "./actions";
import axios from "axios";
import { RootState } from "../root-reducer";
import { deepAssign, getNestedValue } from "../../helper";
import { mapStoreKeyToZohoField } from "./helpers";

function* createClaimFlow() {
  while (true) {
    const { payload } = yield take(ClaimAction.CREATE_CLAIM);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const claimState = yield select((state) => state.claim);
      const body = {
        ...mapCustomerInfoToApi(claimState),
        ...payload,
      };
      const data = yield call(API.fileClaim.upsertClaim, body);
      if (data?.status?.toLowerCase() === "success") {
        const zohoClaimId = data?.details?.id;
        yield put(updateClaimState({ id: zohoClaimId }));
        nextStepRedirect({ id: zohoClaimId }, 2);
      } else {
        throw new Error("Failed to create claim");
      }
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* removeClaimImageFlow() {
  while (true) {
    const { payload } = yield take(ClaimAction.REMOVE_CLAIM_IMAGE);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const { fileName, storeKeyName } = payload;
      const claimId = yield select((state: RootState) => state.claim.id);
      yield call(API.fileClaim.deleteClaimImage, claimId, fileName);
      const claim = yield select((state: RootState) => state.claim);
      const oldValue = getNestedValue(claim, storeKeyName);
      const newValues = oldValue.filter(
        (fileUrl) => !fileUrl.includes(fileName)
      );
      const updatedStore = deepAssign(
        storeKeyName,
        newValues,
        structuredClone(claim)
      );
      yield put(updateClaimState(updatedStore));

      const zohoFieldName = mapStoreKeyToZohoField(storeKeyName);
      if (zohoFieldName) {
        yield call(API.fileClaim.upsertClaim, {
          id: claimId,
          [zohoFieldName]: Array.isArray(newValues)
            ? newValues.join("\n\n")
            : newValues,
        });
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* fetchClaimFlow() {
  while (true) {
    const { payload: claimId } = yield take(ClaimAction.FETCH_CLAIM);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const claimData = yield call(API.fileClaim.getClaim, claimId);
      if (claimData) {
        const documents = mapZohoToDocuments(claimData);
        const claim = yield select((state) => state.claim);

        yield put(
          updateClaimState({
            documents,
            purchaseInfo: {
              ...claim.purchaseInfo,
              invoice_image: claimData?.Invoice_Image || "",
            },
          })
        );
      }
    } catch (error) {
      console.log("error fetching claim:", error);
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* uploadClaimImageFlow() {
  while (true) {
    const { payload = {} } = yield take(ClaimAction.UPLOAD_CLAIM_IMAGE);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const { files, isMultiple, storeKeyName } = payload;
      const claim = yield select((state: RootState) => state?.claim || {});
      const claimId = yield select((state: RootState) => state.claim.id);
      if (!isMultiple) {
        const url = getNestedValue(claim, storeKeyName);
        if (url) {
          const fileName = url?.split("/")?.pop();
          yield call(API.fileClaim.deleteClaimImage, claimId, fileName);
        }
      }
      const uploadedUrlList: string[] = yield all(
        files.map(function* (file) {
          const uploadURL = yield call(
            API.fileClaim.getPresignedURL,
            claimId,
            file?.type
          );
          yield axios.put(uploadURL, file, {
            headers: {
              "Content-Type": file.type,
            },
          });
          const urlObj = new URL(uploadURL);
          urlObj.search = "";
          return urlObj.toString();
        })
      );
      const oldValues = getNestedValue(claim, storeKeyName);
      const newValues = isMultiple
        ? oldValues.concat(uploadedUrlList)
        : uploadedUrlList[0];
      const updtedStore = deepAssign(
        storeKeyName,
        newValues,
        structuredClone(claim)
      );
      yield put(updateClaimState(updtedStore));

      // Persist directly to Zoho CRM
      const zohoFieldName = mapStoreKeyToZohoField(storeKeyName);
      if (zohoFieldName) {
        yield call(API.fileClaim.upsertClaim, {
          id: claimId,
          [zohoFieldName]: isMultiple ? newValues.join("\n\n") : newValues,
        });
      }
    } catch (error) {
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

function* submitClaimFlow() {
  while (true) {
    yield take(ClaimAction.SUBMIT_CLAIM);
    yield put(enableScopeLoading(ClaimScope));
    try {
      const claimState = yield select((state) => state.claim);
      // const claimName = yield select((state) => state?.claim.name || "");
      // yield call(API.fileClaim.upsertClaim, body);
      const data = yield call(
        API.fileClaim.upsertClaim,
        mapApiClaimData(claimState)
      );
      console.log("data:", data);
      if (data?.status?.toLowerCase() === "success") {
        const claimId = data?.details?.id;
        const claimData = yield call(API.fileClaim.getClaim, claimId);
        const claimName = claimData?.Name;
        const statusCode = claimData?.status_code;
        yield put(
          updateClaimState({
            name: claimName,
            status_code: statusCode,
          })
        );
        const email = yield select(
          (state: RootState) =>
            state?.claim?.customerInfo?.email || "no@email.com"
        );
        yield call(API.fileClaim.createDeskTicket, {
          claimId,
          contact_email: email,
          subject: `Review New Mattress Claim: ${claimName}`,
          description: makeHTMLContentForTicket(claimState, claimName),
        });
        // Run the "Submit Claim" blueprint transition
        yield call(API.fileClaim.runBlueprint, claimId);
      }
      nextStepRedirect(6);
    } catch (error) {
      console.log("error:", error);
      yield put({
        type: ScopeErrorAction.SET_SCOPE_ERROR,
        payload: ClaimScope,
        error,
      });
    } finally {
      yield put(disableScopeLoading(ClaimScope));
    }
  }
}

export default function* claimSaga() {
  yield fork(createClaimFlow);
  yield fork(submitClaimFlow);
  yield fork(removeClaimImageFlow);
  yield fork(uploadClaimImageFlow);
  yield fork(fetchClaimFlow);
}
